import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';

const PriceOracleContext = createContext(null);

// Configuration
const PRICE_UPDATE_INTERVAL = 10000; // 10 seconds
const SLIPPAGE_THRESHOLD = 0.05; // 5% slippage protection
const FALLBACK_PRICE = 0.0004; // Fallback price if API fails

// $SLAP Token Mint Address (CA) - PHASE 11 REBRAND
const SLAP_TOKEN_MINT = 'A7rKF3srg28UArWp1ibVVUbQmnjUnitTtq7ZFR4tpump';

// Swap fee for $SLAP -> SOL conversions
const PLATFORM_SWAP_FEE = 0.01; // 1% fee

// FIXED: Arena costs now match the TIERS displayed in the UI
// 3 ARENAS: FISH, SHARK, WHALE
const ARENA_USD_COSTS = {
  fish: 1.00,        // FISH 🐟 - $1.00 entry (Beginner)
  shark: 5.00,       // SHARK 🦈 - $5.00 entry (Advanced)
  whale: 20.00       // WHALE 🐋 - $20.00 entry (High Roller)
};

export const usePriceOracle = () => {
  const context = useContext(PriceOracleContext);
  if (!context) {
    throw new Error('usePriceOracle must be used within a PriceOracleProvider');
  }
  return context;
};

export const PriceOracleProvider = ({ children }) => {
  // Core price state
  const [tokenPrice, setTokenPrice] = useState(FALLBACK_PRICE);
  const [previousPrice, setPreviousPrice] = useState(FALLBACK_PRICE);
  const [priceDirection, setPriceDirection] = useState(null); // 'up' | 'down' | null
  const [lastUpdateTime, setLastUpdateTime] = useState(Date.now());
  const [isLive, setIsLive] = useState(false);
  const [updateCount, setUpdateCount] = useState(0);
  const [priceSource, setPriceSource] = useState('fallback'); // 'live' | 'fallback'
  
  // Arena token costs (calculated from USD / price) - FISH, SHARK, WHALE
  const [arenaCosts, setArenaCosts] = useState({
    fish: Math.ceil(ARENA_USD_COSTS.fish / FALLBACK_PRICE),
    shark: Math.ceil(ARENA_USD_COSTS.shark / FALLBACK_PRICE),
    whale: Math.ceil(ARENA_USD_COSTS.whale / FALLBACK_PRICE)
  });
  
  // Ref to store price at time of user click (for slippage check)
  const priceAtClickRef = useRef(null);
  const intervalRef = useRef(null);
  const failCountRef = useRef(0);

  // Fetch LIVE price from DexScreener API (no auth required)
  const fetchLivePrice = useCallback(async () => {
    try {
      // DexScreener API - free, no auth required
      const response = await fetch(
        `https://api.dexscreener.com/latest/dex/tokens/${SLAP_TOKEN_MINT}`,
        {
          method: 'GET',
          headers: {
            'Accept': 'application/json',
          },
        }
      );

      if (!response.ok) {
        throw new Error(`DexScreener API error: ${response.status}`);
      }

      const data = await response.json();
      
      // Extract price from DexScreener response (first pair's USD price)
      const pairs = data?.pairs;
      
      if (pairs && pairs.length > 0) {
        // Get the pair with highest liquidity
        const bestPair = pairs.sort((a, b) => 
          (b.liquidity?.usd || 0) - (a.liquidity?.usd || 0)
        )[0];
        
        const newPrice = parseFloat(bestPair.priceUsd);
        
        if (newPrice > 0 && !isNaN(newPrice)) {
          // Success! Use live price
          setTokenPrice(currentPrice => {
            setPreviousPrice(currentPrice);
            
            // Set direction
            if (newPrice > currentPrice * 1.001) {
              setPriceDirection('up');
            } else if (newPrice < currentPrice * 0.999) {
              setPriceDirection('down');
            } else {
              setPriceDirection(null);
            }
            
            // Clear direction after animation
            setTimeout(() => setPriceDirection(null), 800);
            
            return newPrice;
          });
          
          setIsLive(true);
          setPriceSource('live');
          setLastUpdateTime(Date.now());
          setUpdateCount(prev => prev + 1);
          failCountRef.current = 0;
          
          console.log(`[PriceOracle] Live price: $${newPrice.toFixed(8)} (${bestPair.baseToken?.symbol})`);
          return true;
        }
      }
      
      throw new Error('No valid pairs found on DexScreener');
      
    } catch (error) {
      console.warn('[PriceOracle] API fetch failed:', error.message);
      failCountRef.current += 1;
      
      // After 3 consecutive failures, use fallback
      if (failCountRef.current >= 3) {
        console.log('[PriceOracle] Using fallback price');
        setPriceSource('fallback');
        setIsLive(false);
        
        // Use fallback but still update timestamp
        setLastUpdateTime(Date.now());
        setUpdateCount(prev => prev + 1);
      }
      
      return false;
    }
  }, []);

  // Recalculate arena costs whenever price changes - FISH, SHARK, WHALE
  useEffect(() => {
    const newCosts = {
      fish: Math.ceil(ARENA_USD_COSTS.fish / tokenPrice),
      shark: Math.ceil(ARENA_USD_COSTS.shark / tokenPrice),
      whale: Math.ceil(ARENA_USD_COSTS.whale / tokenPrice)
    };
    setArenaCosts(newCosts);
  }, [tokenPrice]);

  // Start price fetching loop
  useEffect(() => {
    // Initial fetch
    fetchLivePrice();
    
    // Set up interval
    intervalRef.current = setInterval(() => {
      fetchLivePrice();
    }, PRICE_UPDATE_INTERVAL);
    
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [fetchLivePrice]);

  // Store price when user initiates play action
  const lockPriceForTransaction = useCallback(() => {
    priceAtClickRef.current = {
      price: tokenPrice,
      timestamp: Date.now(),
      arenaCosts: { ...arenaCosts }
    };
    return priceAtClickRef.current;
  }, [tokenPrice, arenaCosts]);

  // Check if slippage exceeds threshold
  const checkSlippage = useCallback(() => {
    if (!priceAtClickRef.current) {
      return { hasSlippage: false, slippagePercent: 0 };
    }
    
    const lockedPrice = priceAtClickRef.current.price;
    const timeSinceLock = Date.now() - priceAtClickRef.current.timestamp;
    
    // Only check if within 4 seconds of lock
    if (timeSinceLock > 4000) {
      return { hasSlippage: true, slippagePercent: 100, reason: 'timeout' };
    }
    
    const priceDiff = Math.abs(tokenPrice - lockedPrice) / lockedPrice;
    const hasSlippage = priceDiff > SLIPPAGE_THRESHOLD;
    
    return {
      hasSlippage,
      slippagePercent: (priceDiff * 100).toFixed(2),
      oldPrice: lockedPrice,
      newPrice: tokenPrice,
      direction: tokenPrice > lockedPrice ? 'up' : 'down'
    };
  }, [tokenPrice]);

  // Clear the locked price (after successful transaction or user confirmation)
  const clearLockedPrice = useCallback(() => {
    priceAtClickRef.current = null;
  }, []);

  // Format price for display
  const formatPrice = useCallback((price) => {
    if (price >= 0.01) return `$${price.toFixed(4)}`;
    if (price >= 0.001) return `$${price.toFixed(5)}`;
    return `$${price.toFixed(6)}`;
  }, []);

  // Calculate token amount for USD value
  const usdToTokens = useCallback((usdAmount) => {
    return Math.ceil(usdAmount / tokenPrice);
  }, [tokenPrice]);

  // Force refresh price
  const refreshPrice = useCallback(() => {
    return fetchLivePrice();
  }, [fetchLivePrice]);

  const value = {
    // Price data
    tokenPrice,
    previousPrice,
    priceDirection,
    lastUpdateTime,
    isLive,
    updateCount,
    priceSource,
    
    // Arena costs (in tokens)
    arenaCosts,
    arenaUsdCosts: ARENA_USD_COSTS,
    
    // Slippage protection
    lockPriceForTransaction,
    checkSlippage,
    clearLockedPrice,
    slippageThreshold: SLIPPAGE_THRESHOLD,
    
    // Utilities
    formatPrice,
    usdToTokens,
    refreshPrice,
    
    // Constants
    updateInterval: PRICE_UPDATE_INTERVAL,
    tokenMint: SLAP_TOKEN_MINT,
    fallbackPrice: FALLBACK_PRICE,
    platformSwapFee: PLATFORM_SWAP_FEE,
  };

  return (
    <PriceOracleContext.Provider value={value}>
      {children}
    </PriceOracleContext.Provider>
  );
};

export default PriceOracleProvider;
